"use client";

import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import AuthChecker from "~/components/auth/auth-checker";
import RootLayout from "~/components/layouts/root-layout";
import { PageLoader } from "~/components/ui/page-loader";
import TypesafeI18n from "~/i18n/i18n-react";
import type { Locales } from "~/i18n/i18n-types";
import { loadAllLocales } from "~/i18n/i18n-util.sync";
import { activeTenant } from "~/tenant-config/active";
import { TenantProvider } from "~/tenant-config/react";
import { TRPCProvider } from "~/trpc/TRPCProvider";
import { useSession } from "~/utils/auth-client";
import { useLocale } from "~/utils/use-locale";

loadAllLocales();

function AuthGate({ children }: { children: ReactNode }) {
	const pathname = usePathname();
	const { data: session, isPending } = useSession();

	if (isPending) {
		return <PageLoader />;
	}

	if (!session && pathname !== "/auth/login") {
		return <AuthChecker />;
	}

	return (
		<>
			<AuthChecker />
			{children}
		</>
	);
}

export function Providers({ children }: { children: ReactNode }) {
	const locale = useLocale() as Locales;

	return (
		<TenantProvider tenant={activeTenant}>
			<TRPCProvider>
				<TypesafeI18n locale={locale}>
					<RootLayout>
						<AuthGate>{children}</AuthGate>
					</RootLayout>
				</TypesafeI18n>
			</TRPCProvider>
		</TenantProvider>
	);
}
